import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { field, refuse } from "../clients/forms";

// Settings › Users and roles, levels and jobs (brief 1E-7, mockup v59): the Starter roles card
// (Admin, Manager, Worker and the Account owner chip), the Jobs table (job, level, people, given
// to new people, View as and Copy into a new job, with "View as ends after … minutes"), and the
// More than one job card with the access review note. The server decides; every change is in the
// history.

export interface JobRow {
  id: string | null;
  roleId: string | null;
  name: string;
  level: "admin" | "manager" | "worker";
  people: number;
  givenTo: string | null;
  field: boolean;
  module: string;
  fixed: boolean;
}
export interface JobsBody {
  starter: { level: string; label: string; who: string; sees: string; money: string }[];
  owners: { id: string; name: string }[];
  isOwner: boolean;
  jobs: JobRow[];
  multi: { id: string; name: string; jobs: string[]; extras: string[]; gives: string }[];
  review: { days: number; last: string | null; lastWords: string; dueOn: string; due: boolean };
  viewAsMinutes: number;
  error?: string;
}

const LEVEL_WORD = { admin: "Admin", manager: "Manager", worker: "Worker" } as const;
const levelChip = (l: JobRow["level"]) =>
  chip(LEVEL_WORD[l], l === "admin" ? "warn" : l === "manager" ? "info" : "plain");

/** A number box that saves on change (Admin): "View as ends after [30] minutes". */
function numberSetting(
  label: [string, string],
  value: number,
  limits: [number, number],
  canEdit: boolean,
  save: (n: number) => Promise<string | null>,
): HTMLElement {
  const input = el("input", {
    type: "number",
    min: String(limits[0]),
    max: String(limits[1]),
    value: String(value),
    disabled: !canEdit,
    "aria-label": `${label[0]} … ${label[1]}`.trim(),
    style: "width:5.5em",
  });
  input.addEventListener("change", async () => {
    input.disabled = true;
    const error = await save(Number(input.value));
    input.disabled = false;
    if (error) {
      input.value = String(value);
      return toast("That wasn't saved", error);
    }
    value = Number(input.value);
    toast("Saved", `${label[0]} ${value} ${label[1]}`.trim());
  });
  return el(
    "label",
    { class: "small", style: "display:inline-flex;gap:6px;align-items:center;flex-wrap:wrap" },
    label[0],
    input,
    label[1],
  );
}

export function jobsCards(
  j: JobsBody,
  opts: {
    canAdmin: boolean;
    viewAs: (job: JobRow) => void;
    reload: (next?: JobsBody) => void;
  },
): HTMLElement[] {
  const owners = j.owners.map((o) => o.name).join(", ");
  // ---- Starter roles ----
  const starter = el(
    "section",
    { class: "card", "aria-label": "Starter roles" },
    el(
      "div",
      { class: "cardhead" },
      el(
        "div",
        {},
        el("h2", {}, icon("shield-check"), " Starter roles"),
        el("span", { class: "small muted" }, "Three levels. Every job sits on one of them."),
      ),
    ),
    table<JobsBody["starter"][number]>(
      [
        {
          h: "Level",
          f: (r) =>
            el(
              "span",
              {},
              el("b", {}, r.label),
              r.level === "admin" && owners
                ? el("span", {}, " ", chip(`Account owner: ${owners}`, "info"))
                : null,
            ),
        },
        { h: "Who", f: (r) => r.who },
        { h: "Sees", f: (r) => r.sees },
        { h: "Pay and billing", f: (r) => r.money },
      ],
      j.starter,
      undefined,
      "Starter roles",
    ),
  );

  // ---- Jobs ----
  const jobs = el(
    "section",
    { class: "card", "aria-label": "Jobs" },
    el(
      "div",
      { class: "cardhead" },
      el(
        "div",
        {},
        el("h2", {}, icon("people"), " Jobs"),
        el(
          "span",
          { class: "small muted" },
          "The company's names for the work people do. Each starts from a level.",
        ),
      ),
    ),
    table<JobRow>(
      [
        { h: "Job", f: (r) => el("b", {}, r.name) },
        { h: "Level", f: (r) => levelChip(r.level) },
        { h: "People", num: true, f: (r) => String(r.people) },
        {
          h: "Given to new people",
          f: (r) => (r.givenTo ? chip(r.givenTo, "ok") : el("span", { class: "dot" }, "–")),
        },
        {
          h: "",
          f: (r) => {
            if (r.fixed)
              return el(
                "span",
                {
                  class: "chip plain",
                  "data-tip": "Admin is fixed: it sees everything and can't be changed",
                },
                icon("lock"),
                " Locked",
              );
            return el(
              "span",
              { class: "actions" },
              opts.canAdmin && r.level !== "admin"
                ? ib(
                    "eye",
                    `View as ${r.name} (read-only, ${j.viewAsMinutes} minutes, recorded)`,
                    () => opts.viewAs(r),
                  )
                : null,
              ib("copy", `Copy ${r.name} into a new job`, () => copyJob(r, opts.reload)),
            );
          },
        },
      ],
      j.jobs,
      undefined,
      "Jobs",
    ),
    el(
      "p",
      { class: "small muted" },
      "Copy from a job. Copying from a person would carry their one-off extras to someone new.",
    ),
    el(
      "div",
      { style: "margin-top:6px" },
      numberSetting(
        ["View as ends after", "minutes"],
        j.viewAsMinutes,
        [5, 240],
        opts.canAdmin,
        async (n) => {
          const r = await api<JobsBody>("PUT", "/api/settings/jobs-settings", {
            viewAsMinutes: n,
          });
          if (!r.ok) return r.body.error ?? "That wasn't saved.";
          opts.reload(r.body);
          return null;
        },
      ),
    ),
  );

  // ---- More than one job ----
  const multi = el(
    "section",
    { class: "card", "aria-label": "More than one job" },
    el(
      "div",
      { class: "cardhead" },
      el(
        "div",
        {},
        el("h2", {}, icon("person"), " More than one job"),
        el("span", { class: "small muted" }, "Access adds up. Each extra is shown on the person."),
      ),
    ),
    j.multi.length
      ? table<JobsBody["multi"][number]>(
          [
            { h: "Person", f: (r) => el("b", {}, r.name) },
            {
              h: "Jobs",
              f: (r) =>
                el(
                  "span",
                  { style: "display:inline-flex;gap:4px;flex-wrap:wrap" },
                  ...r.jobs.map((x) => chip(x, "info")),
                ),
            },
            {
              h: "What that gives",
              f: (r) =>
                el(
                  "span",
                  {},
                  r.gives,
                  r.extras.length
                    ? el("div", { class: "small muted" }, `Extras: ${r.extras.join(", ")}`)
                    : null,
                ),
            },
          ],
          j.multi,
          undefined,
          "More than one job",
        )
      : el("p", { class: "small muted" }, "Nobody has more than one job yet."),
    el(
      "p",
      { class: "small muted" },
      `Access review every ${j.review.days} days. Last review: ${j.review.lastWords}.`,
    ),
    opts.canAdmin
      ? numberSetting(["Access review every", "days"], j.review.days, [7, 365], true, async (n) => {
          const r = await api<JobsBody>("PUT", "/api/settings/jobs-settings", {
            accessReviewDays: n,
          });
          if (!r.ok) return r.body.error ?? "That wasn't saved.";
          opts.reload(r.body);
          return null;
        })
      : null,
  );
  return [starter, jobs, multi];
}

/** Copy into a new job: name it; the copy starts with the same areas, switches, scope and level. */
function copyJob(job: JobRow, reload: (next?: JobsBody) => void) {
  if (!job.id) return;
  const name = field("Name", "", { maxlength: "40" });
  const go = el("button", { type: "button", class: "btn pri" }, icon("copy"), " Make the copy");
  const m = openModal({
    title: `Copy of ${job.name}`,
    sub: "Name it; the copy starts with the same areas, switches, scope and level.",
    body: [el("div", { class: "form" }, name.el)],
    actions: [go],
  });
  name.input.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api<JobsBody & { copied?: { name: string } }>(
      "POST",
      `/api/settings/jobs/${job.id}/copy`,
      { name: name.input.value },
    );
    go.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(
      `${r.body.copied?.name ?? "The copy"} made`,
      `From ${job.name}; change it from the Jobs table`,
    );
    reload(r.body);
  });
}
